import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {CogIcon, ClockIcon} from '@heroicons/react/24/outline';
import {selectBlurSensitive} from '../slices/preferencesSlice';
import {selectAuthIsAdmin, selectAuthIsMarketOwner} from '../slices/authSlice';
import {
  fetchHorizonComparison,
  selectHorizonComparison,
  selectHorizonComparisonLoading,
  selectAvailableHorizons,
} from '../slices/marketSlice';
import {ENSEMBLE_MODELS} from '../constants/ensembleModels';
import {ForecasterPicker} from './ForecasterPicker';
import FilterChip from './FilterChip';
import AnimationCircle from './AnimationCircle';

const MODEL_LABEL = Object.fromEntries(ENSEMBLE_MODELS.map((m) => [m.value, m.label]));
const MODEL_SHORT = Object.fromEntries(ENSEMBLE_MODELS.map((m) => [m.value, m.short]));

const horizonLabel = (h) => (Number(h) === 0 ? 'Intraday' : `D+${h}`);

// Signed fractional change of `other` relative to the baseline `primary`.
// Negative ⇒ lower error than baseline ⇒ better (rendered green ▲).
function relImprovement(primary, other) {
  if (primary == null || other == null || primary === 0) return null;
  return (other - primary) / primary;
}

function DeltaBadge({value}) {
  if (value === null || value === undefined) return null;
  const pct = (value * 100).toFixed(1);
  const better = value < 0;
  return (
    <span className={`ml-1 text-xs font-medium ${better ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
      ({better ? '▲' : '▼'} {Math.abs(pct)}%)
    </span>
  );
}

function ScoreCell({block, improvement, commonDays}) {
  const v = block?.avg_value;
  if (v === null || v === undefined) {
    return <td className="px-4 py-3 text-left text-gray-400 dark:text-gray-500 text-sm">—</td>;
  }
  const own = block?.own_days;
  const title = commonDays != null
    ? `Averaged over ${commonDays} common day${commonDays === 1 ? '' : 's'}`
      + (own != null && own !== commonDays
        ? ` · this row has ${own} day${own === 1 ? '' : 's'} at this horizon`
        : '')
    : undefined;
  return (
    <td className="px-4 py-3 text-left text-sm font-mono text-gray-800 dark:text-gray-200" title={title}>
      {Number(v).toFixed(4)}
      <DeltaBadge value={improvement} />
    </td>
  );
}

export default function PerHorizonComparison({
  resourceId, startDate, endDate, participants,
  appliedModel, appliedComparisonModels,
}) {
  const dispatch = useDispatch();
  const availableHorizons = useSelector(selectAvailableHorizons);
  const comparison = useSelector(selectHorizonComparison);
  const loading = useSelector(selectHorizonComparisonLoading);
  const blurSensitive = useSelector(selectBlurSensitive);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);

  // Concrete horizons only (no "Latest"). Default to D+1 when available,
  // otherwise the lowest available.
  const horizons = useMemo(
    () => (Array.isArray(availableHorizons) ? [...availableHorizons].sort((a, b) => a - b) : []),
    [availableHorizons],
  );
  const [horizon, setHorizon] = useState(null);
  useEffect(() => {
    if (horizon == null && horizons.length > 0) {
      setHorizon(horizons.includes(1) ? 1 : horizons[0]);
    }
  }, [horizons, horizon]);

  // Model selection mirrors the main plot's Model dropdown: one primary
  // (the delta baseline) + any number of comparison strategies. Seeded once
  // from the plot's applied selection, independent thereafter.
  const [primaryModel, setPrimaryModel] = useState(appliedModel || null);
  const [comparisonModels, setComparisonModels] = useState(appliedComparisonModels || []);
  const [userIds, setUserIds] = useState([]);

  // The set of ensemble strategies to score = primary + comparisons (unique).
  const models = useMemo(
    () => Array.from(new Set([primaryModel, ...comparisonModels].filter(Boolean))),
    [primaryModel, comparisonModels],
  );

  // One-popover-at-a-time coordination for the chip row, matching the plot.
  const [openChip, setOpenChip] = useState(null); // 'forecasters' | 'model' | 'horizon' | null
  const chipBarRef = useRef(null);
  useEffect(() => {
    if (!openChip) return undefined;
    const onOutside = (e) => {
      if (chipBarRef.current && !chipBarRef.current.contains(e.target)) setOpenChip(null);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [openChip]);

  const selectPrimary = (value) => {
    setPrimaryModel(value);
    setComparisonModels((prev) => prev.filter((v) => v !== value));
    setOpenChip(null);
  };
  const toggleComparison = (value) =>
    setComparisonModels((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);

  useEffect(() => {
    if (!resourceId || !startDate || !endDate || horizon == null) return;
    if (models.length === 0 && userIds.length === 0) return;
    dispatch(fetchHorizonComparison({
      resource: resourceId, startDate, endDate, horizonOffset: horizon,
      models, userIds,
    }));
  }, [dispatch, resourceId, startDate, endDate, horizon, models, userIds]);

  const displayLabel = (uid) => {
    const f = (participants || []).find((p) => String(p.user_id) === String(uid));
    if (!f) return uid;
    const useEmail = isAdmin || isMarketOwner;
    const username = f.user_username ?? f.username;
    const email = f.user_email ?? f.email;
    return blurSensitive ? (username || email) : (useEmail ? (email || username) : username);
  };

  const forecasterOptions = (participants || []).map((p) => ({
    id: String(p.user_id), name: displayLabel(p.user_id),
  }));

  const rows = comparison?.rows || [];
  const rmseCommon = comparison?.common_days?.['rmse:q50'] ?? null;
  const mwiCommon = comparison?.common_days?.['winkler:q10'] ?? null;

  // A row has scored data at this horizon when it reports any own days. When
  // NO row has any (e.g. the period is too recent / observations not in yet),
  // there simply are no scores — distinct from "scores exist but don't overlap".
  const hasAnyScores = rows.some(
    (r) => (r.rmse_q50?.own_days ?? 0) > 0 || (r.winkler_q10?.own_days ?? 0) > 0,
  );

  // RMSE and MWI share the same scored days (forecasters submit q10/q50/q90
  // together), so show one count. Fall back to per-metric only if they ever
  // diverge. Hidden when nothing is scored — "common days" is meaningless then.
  const commonDaysLabel = (() => {
    if (!hasAnyScores) return null;
    if (rmseCommon == null && mwiCommon == null) return null;
    if (rmseCommon == null) return `${mwiCommon} common days`;
    if (mwiCommon == null) return `${rmseCommon} common days`;
    if (rmseCommon === mwiCommon) return `${rmseCommon} common days`;
    return `${rmseCommon} (RMSE) / ${mwiCommon} (MWI) common days`;
  })();

  // Baseline = the chosen primary model's row (fallback: first ensemble, then
  // first row) for the delta badges.
  const baseline =
    rows.find((r) => r.kind === 'ensemble' && r.id === primaryModel)
    || rows.find((r) => r.kind === 'ensemble')
    || rows[0]
    || null;
  const baseRmse = baseline?.rmse_q50?.avg_value ?? null;
  const baseMwi = baseline?.winkler_q10?.avg_value ?? null;

  const rowLabel = (r) => (r.kind === 'ensemble' ? (MODEL_LABEL[r.id] || r.id) : displayLabel(r.id));

  const noScores = rows.length > 0 && !hasAnyScores;
  const noOverlap = rows.length > 0 && hasAnyScores && rmseCommon === 0 && mwiCommon === 0;

  const modelChipValue =
    (primaryModel ? (MODEL_SHORT[primaryModel] || primaryModel) : 'None')
    + (comparisonModels.length > 0 ? ` +${comparisonModels.length}` : '');

  return (
    <div className="rounded-lg bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-sm">
      <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-700">
        <div ref={chipBarRef} className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Per-Horizon Comparison
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {startDate && endDate ? `${startDate} → ${endDate}` : ''} · {horizon != null ? horizonLabel(horizon) : '—'}
              {commonDaysLabel ? ` · ${commonDaysLabel}` : ''}
            </p>
          </div>

          {/* Same chip dropdowns as the main plot toolbar */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ForecasterPicker
              forecasters={forecasterOptions}
              selectedIds={userIds}
              onChange={(ids) => setUserIds(ids == null ? forecasterOptions.map((f) => f.id) : ids)}
              open={openChip === 'forecasters'}
              onOpenChange={(v) => setOpenChip(v ? 'forecasters' : null)}
            />

            {/* Model chip → inline popover (primary + compare) */}
            <div className="relative">
              <FilterChip
                icon={CogIcon}
                label="Model"
                value={modelChipValue}
                active={openChip === 'model'}
                onClick={() => setOpenChip(openChip === 'model' ? null : 'model')}
              />
              {openChip === 'model' && (
                <div className="absolute right-0 z-50 mt-2 w-60 rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-1.5">
                  <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    Primary model (baseline)
                  </p>
                  {ENSEMBLE_MODELS.map((m) => {
                    const isPrimary = primaryModel === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => selectPrimary(m.value)}
                        className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-sm ${
                          isPrimary
                            ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-semibold'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span>{m.label}</span>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">{m.short}</span>
                      </button>
                    );
                  })}
                  <div className="mt-1 border-t border-gray-100 dark:border-gray-700 pt-1">
                    <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Compare
                    </p>
                    {ENSEMBLE_MODELS.filter((m) => m.value !== primaryModel).map((m) => {
                      const on = comparisonModels.includes(m.value);
                      return (
                        <button
                          key={m.value}
                          type="button"
                          onClick={() => toggleComparison(m.value)}
                          className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-sm ${
                            on
                              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-semibold'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span>{m.label}</span>
                          <span className="text-xs">{on ? '✓' : '+ compare'}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Horizon chip → inline popover (concrete offsets only) */}
            <div className="relative">
              <FilterChip
                icon={ClockIcon}
                label="Horizon"
                value={horizon != null ? horizonLabel(horizon) : '—'}
                active={openChip === 'horizon'}
                onClick={() => setOpenChip(openChip === 'horizon' ? null : 'horizon')}
              />
              {openChip === 'horizon' && (
                <div className="absolute right-0 z-50 mt-2 w-52 rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-2">
                  <div className="flex flex-wrap gap-1.5">
                    {horizons.length === 0 && (
                      <span className="px-2 py-1 text-xs text-gray-400 dark:text-gray-500">
                        No horizons available
                      </span>
                    )}
                    {horizons.map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => { setHorizon(h); setOpenChip(null); }}
                        className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition-all ${
                          horizon === h
                            ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600 text-brand-700 dark:text-brand-300'
                            : 'border-gray-200/70 dark:border-gray-600/70 text-gray-600 dark:text-gray-400 hover:border-brand-300'
                        }`}
                      >
                        {horizonLabel(h)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-40"><AnimationCircle size={8} /></div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-gray-400 dark:text-gray-500">
          Pick a horizon and at least one strategy or forecaster.
        </div>
      ) : noScores ? (
        <div className="px-4 py-6 text-center text-xs text-gray-400 dark:text-gray-500">
          No scores for this period at this horizon yet, scores appear once the observations are in and scoring has run.
        </div>
      ) : noOverlap ? (
        <div className="px-4 py-6 text-center text-xs text-gray-400 dark:text-gray-500">
          No overlapping scored days across the selected rows at this horizon.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-b-lg">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 w-1/3">Strategy / Forecaster</th>
                <th
                  className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400"
                  title="Q50 RMSE, averaged over the days all selected rows share at this horizon."
                >
                  Q50 RMSE
                </th>
                <th
                  className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400"
                  title="Probabilistic Mean Winkler Interval, averaged over the days all selected rows share at this horizon."
                >
                  Prob. MWI
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((r) => {
                const isBase = baseline && r.kind === baseline.kind && r.id === baseline.id;
                return (
                  <tr key={`${r.kind}:${r.id}`} className={r.kind === 'forecaster' ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
                      {rowLabel(r)}
                    </td>
                    <ScoreCell block={r.rmse_q50} commonDays={rmseCommon} improvement={isBase ? null : relImprovement(baseRmse, r.rmse_q50?.avg_value)} />
                    <ScoreCell block={r.winkler_q10} commonDays={mwiCommon} improvement={isBase ? null : relImprovement(baseMwi, r.winkler_q10?.avg_value)} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
